import React, { useEffect, useRef } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

const OFF = { color: '#546E7A', weight: 0.8, fillColor: '#CFD8DC', fillOpacity: 0.35 };
const ON = { color: '#0D47A1', weight: 2, fillColor: '#1565C0', fillOpacity: 0.5 };
// Centro: zona coperta ma senza nessuno in organico (buco da colmare)
const GAP = { color: '#B45309', weight: 2, dashArray: '5 4', fillColor: '#F59E0B', fillOpacity: 0.45 };

/** Mappa Leaflet delle zone CAP: confini cliccabili (fallback a punti dove mancano i poligoni). */
export const CapMap = ({ center, points, zones, selected, onToggle, height = 340, radiusCenter = null, radiusKm = 0, staffCounts = null }) => {
  const styleFor = (cap) => {
    if (!selected.includes(cap)) return OFF;
    if (staffCounts && !(staffCounts[cap] > 0)) return GAP;
    return ON;
  };
  const ref = useRef(null);
  const mapRef = useRef(null);
  const layerRef = useRef(null);
  const fittedRef = useRef(null);

  useEffect(() => {
    if (!ref.current || mapRef.current) return undefined;
    const map = L.map(ref.current, { scrollWheelZoom: false, zoomSnap: 0.25, zoomDelta: 0.5 }).setView(center, 11);
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '&copy; OpenStreetMap',
    }).addTo(map);
    mapRef.current = map;
    layerRef.current = L.layerGroup().addTo(map);
    return () => { map.stop(); map.remove(); mapRef.current = null; layerRef.current = null; fittedRef.current = null; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (mapRef.current && center && !fittedRef.current) mapRef.current.setView(center, 11);
  }, [center]);

  useEffect(() => {
    const layer = layerRef.current;
    const map = mapRef.current;
    if (!layer || !map) return;
    layer.clearLayers();

    if (radiusCenter && radiusKm) {
      L.circle(radiusCenter, { radius: radiusKm * 1000, color: '#1565C0', fillOpacity: 0.04, weight: 1, dashArray: '4 4' }).addTo(layer);
    }

    const feats = zones || [];
    if (feats.length) {
      const gj = L.geoJSON(
        { type: 'FeatureCollection', features: feats },
        {
          style: (f) => styleFor(f.properties.cap),
          onEachFeature: (f, lyr) => {
            const cap = f.properties.cap;
            const on = selected.includes(cap);
            const n = staffCounts ? (staffCounts[cap] || 0) : null;
            const label = n === null ? cap : `${cap} · ${n} in organico`;
            lyr.bindTooltip(label, {
              permanent: on,
              direction: 'center',
              className: on ? (n === 0 ? 'cap-tooltip cap-tooltip-gap' : 'cap-tooltip cap-tooltip-on') : 'cap-tooltip',
            });
            lyr.on('click', () => onToggle(cap));
            lyr.on('mouseover', () => lyr.setStyle({ weight: 3, fillOpacity: on ? 0.6 : 0.32 }));
            lyr.on('mouseout', () => lyr.setStyle(styleFor(cap)));
          },
        },
      ).addTo(layer);
      const key = `${feats.length}|${feats[0]?.properties?.comune || ''}`;
      if (fittedRef.current !== key) {
        fittedRef.current = key;
        const bounds = gj.getBounds();
        setTimeout(() => {
          if (!mapRef.current) return;
          mapRef.current.invalidateSize();
          if (bounds.isValid()) mapRef.current.fitBounds(bounds, { padding: [12, 12], animate: false });
        }, 60);
      }
      return;
    }

    (points || []).forEach((p) => {
      const on = selected.includes(p.cap);
      const gap = on && staffCounts && !(staffCounts[p.cap] > 0);
      L.circleMarker([p.latitude, p.longitude], {
        radius: on ? 12 : 9,
        color: gap ? '#B45309' : (on ? '#1565C0' : '#90A4AE'),
        fillColor: gap ? '#F59E0B' : (on ? '#1565C0' : '#CFD8DC'),
        fillOpacity: 0.85,
        weight: 2,
      })
        .bindTooltip(p.cap, {
          permanent: on,
          direction: 'top',
          offset: [0, -6],
          className: on ? 'cap-tooltip cap-tooltip-on' : 'cap-tooltip',
        })
        .on('click', () => onToggle(p.cap))
        .addTo(layer);
    });
  }, [points, zones, selected, onToggle, radiusCenter, radiusKm, staffCounts]); // eslint-disable-line react-hooks/exhaustive-deps

  return <div ref={ref} style={{ height, width: '100%' }} data-testid="cap-map" />;
};

export default CapMap;
